import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, Sparkles, ScanLine, User, Wrench, Plus, Trash2,
  Send, DollarSign, Save, CheckCircle2, Truck, ArrowLeft,
} from "lucide-react";

const STATUS_LABELS = {
  draft: "Rascunho",
  sent_to_tech: "Com Técnico",
  tech_completed: "Técnico OK",
  sent_to_finance: "No Financeiro",
  paid: "Pago",
  closed: "Fechado",
};

export default function FullTimeOrderForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== "new";

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [vinBusy, setVinBusy] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  const [order, setOrder] = useState({
    id: null, order_number: null, status: "draft",
    vin: "", vehicle_year: "", vehicle_make: "", vehicle_model: "", vehicle_trim: "",
    customer_name: "", customer_email: "", customer_phone: "", customer_address: "",
    customer_ro_number: "", fulltime_reference: "", notes: "",
    lines: [], tax_pct: 0, scheduled_at: "", technician_id: "",
    subtotal: 0, tax: 0, total: 0,
    calibration_report_id: null, invoice_id: null, invoice_number: null,
  });

  useEffect(() => {
    (async () => {
      try {
        let users = [];
        try { users = (await api.get("/users/roster")).data || []; }
        catch { users = (await api.get("/admin/users")).data || []; }
        setTechnicians(users.filter((u) => (u.role || "").toLowerCase() === "technician"));
      } catch { /* noop */ }
      if (isEdit) {
        try {
          const r = await api.get(`/fulltime-orders/${id}`);
          setOrder({ ...order, ...r.data });
        } catch { toast.error("Ordem não encontrada"); navigate("/fulltime"); }
        finally { setLoading(false); }
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  const decodeAndAnalyze = async () => {
    const vin = (order.vin || "").toUpperCase().trim();
    if (vin.length < 11) return toast.error("VIN mínimo 11 caracteres");
    setVinBusy(true);
    try {
      const dec = await api.get(`/vin/decode/${vin}`);
      const d = dec.data || {};
      const ana = await api.post("/fulltime-orders/analyze-vin", {
        vin,
        collision_damage: true, post_wheel_alignment: true,
      });
      const a = ana.data || {};
      const newLines = (a.operations || []).map((op) => ({
        op_key: op.op_key, op_label: op.op_label,
        cal_type: op.cal_type || "static",
        unit_price: op.unit_price, quantity: 1,
        amount: op.amount, hours: op.hours || 0,
        confidence: op.confidence,
      }));
      setOrder({
        ...order,
        vin,
        vehicle_year: d.year || a.vehicle?.year || "",
        vehicle_make: d.make || a.vehicle?.make || "",
        vehicle_model: d.model || a.vehicle?.model || "",
        vehicle_trim: d.trim === "—" ? "" : (d.trim || a.vehicle?.trim || ""),
        lines: [...order.lines, ...newLines],
      });
      toast.success(`Decoded + ${newLines.length} ops sugeridas ($${a.total?.toFixed(2)})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha VIN/análise");
    } finally { setVinBusy(false); }
  };

  const updateLine = (idx, key, val) => {
    const next = [...order.lines];
    next[idx][key] = ["unit_price", "amount", "quantity", "hours"].includes(key) ? parseFloat(val) || 0 : val;
    if (key === "unit_price" || key === "quantity") {
      next[idx].amount = (parseFloat(next[idx].unit_price) || 0) * (parseFloat(next[idx].quantity) || 1);
    }
    setOrder({ ...order, lines: next });
  };
  const addLine = () => setOrder({ ...order, lines: [...order.lines, { op_label: "", cal_type: "static", unit_price: 0, quantity: 1, amount: 0, hours: 0 }] });
  const removeLine = (idx) => setOrder({ ...order, lines: order.lines.filter((_, i) => i !== idx) });

  const subtotal = order.lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const tax = subtotal * (parseFloat(order.tax_pct) || 0) / 100;
  const total = subtotal + tax;

  const buildPayload = () => ({
    vin: order.vin || null,
    vehicle_year: order.vehicle_year ? parseInt(order.vehicle_year) : null,
    vehicle_make: order.vehicle_make || null,
    vehicle_model: order.vehicle_model || null,
    vehicle_trim: order.vehicle_trim || null,
    customer_name: order.customer_name, customer_email: order.customer_email || null,
    customer_phone: order.customer_phone || null, customer_address: order.customer_address || null,
    customer_ro_number: order.customer_ro_number || null,
    fulltime_reference: order.fulltime_reference || null, notes: order.notes || null,
    lines: order.lines.map((l) => ({
      op_key: l.op_key || null, op_label: l.op_label, cal_type: l.cal_type || "static",
      unit_price: parseFloat(l.unit_price) || 0, quantity: parseFloat(l.quantity) || 1,
      amount: parseFloat(l.amount) || 0, hours: parseFloat(l.hours) || 0,
      confidence: l.confidence || null,
    })),
    tax_pct: parseFloat(order.tax_pct) || 0,
    scheduled_at: order.scheduled_at || null, technician_id: order.technician_id || null,
  });

  const save = async () => {
    if (!order.customer_name) return toast.error("Nome do cliente obrigatório");
    setSaving(true);
    try {
      if (isEdit) {
        const r = await api.patch(`/fulltime-orders/${id}`, buildPayload());
        setOrder({ ...order, ...r.data });
        toast.success("Ordem atualizada");
      } else {
        const r = await api.post("/fulltime-orders", buildPayload());
        toast.success(`Ordem ${r.data.order_number} criada`);
        navigate(`/fulltime/${r.data.id}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar"); }
    setSaving(false);
  };

  const sendToTech = async () => {
    if (!order.lines.length) return toast.error("Adicione ao menos 1 operação");
    if (!window.confirm(`Enviar Ordem ${order.order_number} ao técnico?\n\nUma Work Order será criada na fila.`)) return;
    setSaving(true);
    try {
      // Save first if there are pending changes
      await api.patch(`/fulltime-orders/${id}`, buildPayload());
      const r = await api.post(`/fulltime-orders/${id}/send-to-tech`);
      toast.success(`Enviado ao técnico · WO #${r.data.calibration_report_id.slice(0, 8)}`, {
        description: "Redirecionando para a Work Order...", duration: 4000,
      });
      navigate(`/work-orders/${r.data.calibration_report_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
    setSaving(false);
  };

  const sendToFinance = async () => {
    if (!window.confirm(`Enviar para o Financeiro?\n\nUma invoice FullTime será gerada (INV-FT-YYYY-XXXXX).`)) return;
    setSaving(true);
    try {
      const r = await api.post(`/fulltime-orders/${id}/send-to-finance`);
      toast.success(`Invoice ${r.data.invoice_number} criada`);
      const u = await api.get(`/fulltime-orders/${id}`);
      setOrder({ ...order, ...u.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
    setSaving(false);
  };

  const isLocked = ["sent_to_finance", "paid", "closed"].includes(order.status);

  if (loading) return (
    <div className="flex items-center justify-center p-12 text-slate-500">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...
    </div>
  );

  return (
    <div className="space-y-5 animate-slide-up max-w-6xl" data-testid="fulltime-order-form">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => navigate("/fulltime")} className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold hover:text-slate-300 flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Ordens FullTime
          </button>
          <h1 className="font-display text-4xl font-semibold tracking-tight flex items-center gap-3">
            <Truck className="w-8 h-8 text-emerald-400" />
            {isEdit ? `Ordem ${order.order_number}` : "Nova Ordem FullTime"}
          </h1>
          {isEdit && (
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-blue-500/15 border border-blue-500/40 text-blue-300 text-[10px] uppercase">
                {STATUS_LABELS[order.status] || order.status}
              </Badge>
              {order.calibration_report_id && (
                <a href={`/work-orders/${order.calibration_report_id}`} className="text-[10px] text-emerald-300 hover:underline">
                  → Ver Work Order
                </a>
              )}
              {order.invoice_number && (
                <span className="text-[10px] font-mono text-amber-300">Invoice: {order.invoice_number}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isLocked && (
            <Button onClick={save} disabled={saving} variant="outline" className="border-slate-700" data-testid="ft-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          )}
          {isEdit && order.status === "draft" && (
            <Button onClick={sendToTech} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="ft-send-tech-btn">
              <Send className="w-4 h-4 mr-2" /> Enviar ao Técnico
            </Button>
          )}
          {isEdit && ["tech_completed", "sent_to_tech"].includes(order.status) && (
            <Button onClick={sendToFinance} disabled={saving} className="bg-amber-600 hover:bg-amber-500" data-testid="ft-send-finance-btn">
              <DollarSign className="w-4 h-4 mr-2" /> Enviar ao Financeiro
            </Button>
          )}
        </div>
      </div>

      {/* 1) VIN block */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 space-y-3">
        <div className="flex items-center gap-2"><ScanLine className="w-5 h-5 text-blue-400" /><h2 className="font-display text-lg">1. VIN + Veículo</h2></div>
        <div className="flex gap-2">
          <Input
            value={order.vin || ""}
            onChange={(e) => setOrder({ ...order, vin: e.target.value.toUpperCase() })}
            placeholder="17-char VIN"
            className="bg-[#030712] border-slate-800 font-mono"
            disabled={isLocked}
            data-testid="ft-vin-input"
          />
          <Button onClick={decodeAndAnalyze} disabled={vinBusy || isLocked} className="bg-blue-600 hover:bg-blue-500" data-testid="ft-decode-btn">
            {vinBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Decode + Analisar
          </Button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Ano</Label><Input value={order.vehicle_year || ""} onChange={(e) => setOrder({ ...order, vehicle_year: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isLocked} /></div>
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Marca</Label><Input value={order.vehicle_make || ""} onChange={(e) => setOrder({ ...order, vehicle_make: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isLocked} /></div>
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Modelo</Label><Input value={order.vehicle_model || ""} onChange={(e) => setOrder({ ...order, vehicle_model: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isLocked} /></div>
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Trim</Label><Input value={order.vehicle_trim || ""} onChange={(e) => setOrder({ ...order, vehicle_trim: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isLocked} /></div>
        </div>
      </div>

      {/* 2) Operações */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Wrench className="w-5 h-5 text-amber-400" /><h2 className="font-display text-lg">2. Operações ({order.lines.length})</h2></div>
          {!isLocked && <Button size="sm" variant="outline" onClick={addLine} className="border-slate-700" data-testid="ft-add-line"><Plus className="w-3.5 h-3.5 mr-1" />Adicionar</Button>}
        </div>
        {order.lines.length === 0 && <div className="text-sm text-slate-500 border border-dashed border-slate-800 rounded-lg p-6 text-center">Nenhuma operação. Clique em <strong className="text-blue-300">Decode + Analisar</strong> ou <strong className="text-blue-300">Adicionar</strong>.</div>}
        {order.lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center py-1.5 border-b border-slate-800/50 last:border-0" data-testid={`ft-line-${i}`}>
            <Input value={l.op_label || ""} onChange={(e) => updateLine(i, "op_label", e.target.value)} placeholder="Descrição" className="col-span-5 bg-[#030712] border-slate-800 text-sm h-8" disabled={isLocked} />
            <select value={l.cal_type || "static"} onChange={(e) => updateLine(i, "cal_type", e.target.value)} className="col-span-2 bg-[#030712] border border-slate-800 rounded-md p-1 text-xs h-8" disabled={isLocked}>
              <option value="static">Static</option>
              <option value="dynamic">Dynamic</option>
              <option value="dual">Dual</option>
            </select>
            <Input type="number" step="0.01" value={l.unit_price || 0} onChange={(e) => updateLine(i, "unit_price", e.target.value)} className="col-span-2 bg-[#030712] border-slate-800 text-sm h-8 font-mono" disabled={isLocked} />
            <div className="col-span-2 text-right text-emerald-400 font-mono text-sm">${(parseFloat(l.amount) || 0).toFixed(2)}</div>
            {!isLocked && <button onClick={() => removeLine(i)} className="col-span-1 text-slate-500 hover:text-red-400 justify-self-center"><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>

      {/* 3) Cliente FullTime */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 space-y-3">
        <div className="flex items-center gap-2"><User className="w-5 h-5 text-emerald-400" /><h2 className="font-display text-lg">3. Cliente + Referência FullTime</h2></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Nome do cliente *</Label><Input value={order.customer_name || ""} onChange={(e) => setOrder({ ...order, customer_name: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isLocked} data-testid="ft-customer-name" /></div>
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Email</Label><Input value={order.customer_email || ""} onChange={(e) => setOrder({ ...order, customer_email: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isLocked} /></div>
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Telefone</Label><Input value={order.customer_phone || ""} onChange={(e) => setOrder({ ...order, customer_phone: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isLocked} /></div>
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Endereço</Label><Input value={order.customer_address || ""} onChange={(e) => setOrder({ ...order, customer_address: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isLocked} /></div>
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">RO Number (opcional)</Label><Input value={order.customer_ro_number || ""} onChange={(e) => setOrder({ ...order, customer_ro_number: e.target.value })} className="mt-1 bg-[#030712] border-slate-800 font-mono" disabled={isLocked} /></div>
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Referência FullTime (PO/Ticket)</Label><Input value={order.fulltime_reference || ""} onChange={(e) => setOrder({ ...order, fulltime_reference: e.target.value })} className="mt-1 bg-[#030712] border-slate-800 font-mono" disabled={isLocked} data-testid="ft-reference" /></div>
        </div>
        <div className="grid md:grid-cols-3 gap-3 pt-3 border-t border-slate-800">
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Agendado para</Label><Input type="datetime-local" value={order.scheduled_at || ""} onChange={(e) => setOrder({ ...order, scheduled_at: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isLocked} /></div>
          <div>
            <Label className="text-[10px] uppercase font-bold text-slate-500">Técnico (opcional)</Label>
            <select value={order.technician_id || ""} onChange={(e) => setOrder({ ...order, technician_id: e.target.value })} className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-lg p-2 text-sm" disabled={isLocked}>
              <option value="">Deixar na fila (qualquer técnico)</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
            </select>
          </div>
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Tax %</Label><Input type="number" step="0.01" value={order.tax_pct || 0} onChange={(e) => setOrder({ ...order, tax_pct: e.target.value })} className="mt-1 bg-[#030712] border-slate-800 font-mono" disabled={isLocked} /></div>
        </div>
        <Textarea value={order.notes || ""} onChange={(e) => setOrder({ ...order, notes: e.target.value })} placeholder="Notas internas..." className="bg-[#030712] border-slate-800" disabled={isLocked} rows={2} />
      </div>

      {/* Totals */}
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300">Total FullTime</div>
          <div className="text-xs text-slate-400 mt-1">Sem comissão · Sem vendedor</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400">Subtotal: <span className="font-mono text-slate-200">${subtotal.toFixed(2)}</span> · Tax: <span className="font-mono text-slate-200">${tax.toFixed(2)}</span></div>
          <div className="font-display text-4xl font-bold text-emerald-300 font-mono" data-testid="ft-total">${total.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}
